import {PageHeader} from "../menu/PageHeader";
import {Button, Col, Container, ListGroup, Row} from "react-bootstrap";
import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {UsacmContext} from "../App";
import {downloadCourseDocument, getMyCourseOutline, getMyCoursePage} from "../api/CourseApi";
import {getErrorMessageForField} from "../utils/formUtils";
import {CourseVideoPlayer} from "./CourseVideoPlayer";
import {CONF_LABEL_KEY_COURSE, CONF_LABEL_KEY_COURSE_PAGE} from "../constants";
import {getConfLabel, makePlural, titleCase} from "../utils/usacmUtils";

/**
 * A student's view of one course - the list of pages on the left and the selected page's content on the right.
 * The outline comes without the page content, each page is loaded when it is shown (which logs the page view).
 */
export function CourseView({courseId, onClose}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const pageLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE_PAGE));
  const [course, setCourse] = useState(null);
  const [pages, setPages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [page, setPage] = useState(null);
  const [errors, setErrors] = useState([]);
  const contentRef = useRef(null);
  const pageHtmlRef = useRef(null);
  // The {video:key} placeholders in the page's html - [{element, courseVideoId, title, key}]
  const [videoSlots, setVideoSlots] = useState([]);

  useEffect(() => {
    getMyCourseOutline(courseId, (status, data, newErrors) => {
      if (status === 200) {
        setCourse(data.course);
        setPages(data.pages || []);
        setPageIndex(0);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }, [courseId]);

  const pageId = pages[pageIndex]?.id;
  useEffect(() => {
    if (!pageId) {
      setPage(null);
      return;
    }
    getMyCoursePage(pageId, (status, data, newErrors) => {
      if (status === 200) {
        setPage(data);
        setErrors([]);
        contentRef.current?.scrollIntoView({block: 'start', behavior: 'smooth'});
      } else {
        setPage(null);
        setErrors(newErrors);
      }
    });
  }, [pageId]);

  // The server turns each {video:key} in the page into an empty div carrying the video id.  The page html is not
  // React, so once it is in the DOM each of those divs gets a player portaled into it.
  useLayoutEffect(() => {
    const elements = pageHtmlRef.current ?
      Array.from(pageHtmlRef.current.querySelectorAll('div.course-video[data-course-video-id]')) : [];
    setVideoSlots(elements.map((element, index) => ({
      element,
      courseVideoId: element.dataset.courseVideoId,
      title: element.getAttribute('title'),
      key: `${page?.id}-${index}`,
    })));
  }, [page]);

  // The server turns each {doc:key} in the page into a link carrying the document id.  The documents are in a
  // private bucket and need the auth token, so the link can't be followed - the download is done here instead.
  function onContentClick(e) {
    const link = e.target.closest('a[data-course-document-id]');
    if (!link) {
      return;
    }
    e.preventDefault();
    downloadCourseDocument(link.dataset.courseDocumentId, link.dataset.fileName || 'document');
  }

  const hasPrevious = pageIndex > 0;
  const hasNext = pageIndex < pages.length - 1;

  return (
    <div>
      <PageHeader pageTitle={course?.title || courseLabel}/>
      <Container fluid className="usacm-container-wide">
        <Row className="mb-3">
          <Col>
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>
              <FontAwesomeIcon icon="fa-arrow-left" className="me-2"/>Dashboard
            </Button>
          </Col>
        </Row>

        <Row>
          <Col md={4} lg={3} className="mb-3">
            <ListGroup className="course-view-outline">
              {pages.map((outlinePage, index) =>
                <ListGroup.Item key={outlinePage.id}
                                action
                                active={index === pageIndex}
                                onClick={() => setPageIndex(index)}>
                  {outlinePage.title}
                </ListGroup.Item>
              )}
            </ListGroup>
            {course && pages.length === 0 &&
              <div className="text-muted">This {courseLabel.toLowerCase()} has no {makePlural(pageLabel).toLowerCase()} yet.</div>
            }
          </Col>

          <Col md={8} lg={9} ref={contentRef}>
            {page &&
              <div>
                <h3 className="mb-3">{page.title}</h3>
                <div className="course-view-content" onClick={onContentClick} ref={pageHtmlRef}
                     dangerouslySetInnerHTML={{__html: page.html || ''}}/>
                {videoSlots.map(slot => createPortal(
                  <CourseVideoPlayer courseVideoId={slot.courseVideoId} title={slot.title}/>, slot.element, slot.key))}
              </div>
            }

            {getErrorMessageForField(errors, '') &&
              <div className="text-center mb-3 usacm-error-message">
                {getErrorMessageForField(errors, '')}
              </div>
            }

            {pages.length > 0 &&
              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <Button type="button" variant="secondary" disabled={!hasPrevious}
                        onClick={() => setPageIndex(pageIndex - 1)}>
                  <FontAwesomeIcon icon="fa-arrow-left" className="me-2"/>Previous
                </Button>
                <small className="text-muted">{pageLabel} {pageIndex + 1} of {pages.length}</small>
                <Button type="button" disabled={!hasNext} onClick={() => setPageIndex(pageIndex + 1)}>
                  Next<FontAwesomeIcon icon="fa-arrow-right" className="ms-2"/>
                </Button>
              </div>
            }
          </Col>
        </Row>
      </Container>
    </div>
  );
}
